import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Address, AddressHelper } from "@app/address/model/address";
import { AddressUtilityService, LookupAddressParams } from "@app/address/services/address.utility.service";
import { TIMEZONES } from "@app/shared/resources/timezones";
import { BehaviorSubject, Observable, Subject, Subscription, of } from "rxjs";
import { debounceTime, delay, distinctUntilChanged, filter, pairwise, startWith, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { AddressChooserComponent } from "../address-chooser/address-chooser.component";

@Component({
    selector: "address-input",
    templateUrl: "./address-input.component.html",
    styleUrls: ["./address-input.component.scss"],
})
export class AddressInputComponent implements OnInit, OnDestroy {
    // the subject passed in from parent component
    @Input()
    address$: BehaviorSubject<Address>;
    @Input() communities$: Observable<any[] | Error>;
    @Input() builders$: Observable<any[] | Error>;
    @Input() buildingTypes$: Observable<any[] | Error>;
    @Input() addressFound$ = new BehaviorSubject(false);
    @Input() addressFoundFromGoogle$ = new BehaviorSubject(false);
    @Input() isReadOnly = false;

    // passed into component to determine whether we want to include lookup functionality
    @Input() includeLookup = false;
    @Output() onUpdateLookupStatus: EventEmitter<boolean> = new EventEmitter();

    shouldShowAdditionalFields = false;
    lookupComplete = false;
    subscriptions: Subscription[] = [];
    addressForm: FormGroup = this.fb.group({
        id: [],
        address: ["", Validators.required],
        address2: [],
        city: ["", Validators.required],
        state: [""],
        country: ["United States", Validators.required],
        zipcode: ["", Validators.required],
        typeid: [""],
        area: ["", Validators.pattern(/[0-9,.]/)],
        builderid: [],
        communityid: [],
        gateaccesscode: [""],
        coordinates: [""],
        timezone: [undefined],
        timezonename: [""],
        timezoneid: [""],
        builderTypeId: [""],
        crossstreet: ["", Validators.maxLength(100)],
        directions: ["", Validators.maxLength(500)],
    });
    prevBuilder: number;
    prevType: number;
    prevCommunity: number;

    requiredInfo = "This information required for any builder rebate programs";
    hasPostalCodes = true;
    hasStates = true;
    timezones = TIMEZONES;

    private readonly unsubscribe$ = new Subject();

    get fields() {
        return this.addressForm.controls;
    }

    constructor(
        private readonly fb: FormBuilder,
        private readonly addressUtilityService: AddressUtilityService,
        private readonly dialog: MatDialog
    ) {}

    ngOnInit() {
        this.prevCommunity = this.address$.value.communityid;
        this.prevBuilder = this.address$.value.builderid;
        this.prevType = this.address$.value.typeid;
        // update the form whenever the address$ subject emits a value
        if (this.isReadOnly) {
            this.addressForm.disable();
        }
        this.address$.pipe(takeUntil(this.unsubscribe$)).subscribe((address: Address) => {
            if (address) {
                if (address.coordinates) {
                    this.lookupComplete = true;
                    this.onUpdateLookupStatus.emit(this.lookupComplete);
                }
                const timezone = this.timezones.find(zone => zone.value === address.timezoneid);
                this.addressForm.patchValue({
                    ...address,
                    timezone,
                });
                this.hasPostalCodes = AddressHelper.hasPostalCodes(address.country);
                this.hasStates = AddressHelper.hasStates(address.country);
                if (this.hasStates) {
                    this.addressForm.controls.state.setValidators([Validators.required]);
                } else {
                    this.addressForm.controls.state.setValidators([]);
                }
            }
        });

        this.subscribeZipcodeChange();
        this.subscribeCityChange();
        this.watchAddressChange();
    }

    private watchAddressChange() {
        const formVal = this.addressForm.value;

        this.addressForm.valueChanges.pipe(takeUntil(this.unsubscribe$), startWith(formVal), pairwise()).subscribe(([prev, current]) => {
            if (this.isNotCurrentAddress(prev, current)) {
                if (this.addressFoundFromGoogle$.value === true) {
                    this.addressFoundFromGoogle$.next(false);
                }
                const addressValue = { ...current };
                if (this.includeLookup) {
                    // If lookup is required, mark lookup as dirty
                    if (!this.isReadOnly) {
                        this.lookupComplete = false;
                    }
                    this.onUpdateLookupStatus.emit(this.lookupComplete);
                    // Set the adddress to an invalid address to disable save
                    addressValue.coordinates = null;
                    this.checkLookupCriteria(prev, current);
                }
                this.address$.next(addressValue);
            } else if (this.isNotSecondaryAddress(prev, current)) {
                const addressValue = {
                    ...current,
                    prevType: this.prevType,
                    prevBuilder: this.prevBuilder,
                    prevCommunity: this.prevCommunity,
                };
                addressValue.builderid = addressValue.builderid ? +addressValue.builderid : null;
                addressValue.communityid = addressValue.communityid ? +addressValue.communityid : null;
                addressValue.typeid = addressValue.typeid ? +addressValue.typeid : null;
                this.address$.next(addressValue);
            }
        });
    }

    handleAddressChange(address: any) {
        const latitude = address.geometry.location.lat();
        const longitude = address.geometry.location.lng();
        const addr = {
            coordinates: `${latitude}, ${longitude}`,
        } as Address;
        this.addressUtilityService.extractAddressFromGeoResults(address.address_components, addr);
        const timezone = this.addressUtilityService.timezoneFromCoordinates(addr).pipe(
            tap(timezone => {
                addr.timezoneid = timezone?.timeZoneId;
                addr.timezonename = timezone?.timeZoneName;
                this.addressForm.patchValue(addr);
                this.processSelectedAddress(addr);
            })
        );
        timezone.pipe(take(1)).subscribe();
        this.addressForm.patchValue(addr);
        this.addressFound$.next(true);
        this.addressFoundFromGoogle$.next(true);
    }

    checkLookupCriteria(prev, curr) {
        if (prev.country !== curr.country) {
            this.hasPostalCodes = AddressHelper.hasPostalCodes(curr.country);
            this.hasStates = AddressHelper.hasStates(curr.country);
            this.addressForm.patchValue({ coordinates: "" });

            if (!this.hasPostalCodes) {
                this.fields.zipcode.setValidators([]);
                this.fields.zipcode.updateValueAndValidity();
            } else {
                this.fields.zipcode.setValidators([Validators.required]);
                this.fields.zipcode.updateValueAndValidity();
            }
        }
    }

    /**
     * subscribe to zipcode changes and emit after 500ms and only emit if different from previous value
     */
    private subscribeZipcodeChange() {
        if (this.includeLookup) {
            const zipcodeSub = this.addressForm.controls.zipcode.valueChanges
                .pipe(
                    filter(() => this.hasPostalCodes),
                    debounceTime(500),
                    distinctUntilChanged()
                )
                .subscribe(value => {
                    // if we have a valid zip code and street address
                    // and we haven't done the initial lookup, then perform lookup
                    if (value.length >= 5 && this.addressForm.controls.address.value && !this.lookupComplete) {
                        this.lookupAddressByZip("zipcode");
                    }
                });
            this.subscriptions.push(zipcodeSub);
        }
    }

    private subscribeCityChange() {
        if (this.includeLookup) {
            const subscription = this.addressForm.controls.city.valueChanges
                .pipe(
                    filter(() => !this.hasPostalCodes),
                    debounceTime(1000),
                    distinctUntilChanged()
                )
                .subscribe(value => {
                    // if we have a valid city and street address
                    // and we haven't done the initial lookup, then perform lookup
                    if (value.length && this.addressForm.controls.address.value && !this.lookupComplete) {
                        this.lookupAddress("city");
                    }
                });

            this.subscriptions.push(subscription);
        }
    }

    private isNotCurrentAddress(prev, current): boolean {
        return (
            prev.address !== current.address ||
            prev.state !== current.state ||
            prev.city !== current.city ||
            prev.country !== current.country ||
            prev.zipcode !== current.zipcode
        );
    }

    private isNotSecondaryAddress(prev: Address, current: Address): boolean {
        return (
            prev.address2 !== current.address2 ||
            prev.typeid !== current.typeid ||
            prev.area !== current.area ||
            prev.builderid !== current.builderid ||
            prev.communityid !== current.communityid ||
            prev.gateaccesscode !== current.gateaccesscode ||
            prev.crossstreet !== current.crossstreet ||
            prev.directions !== current.directions
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    changeTimezone() {
        const { timezone, ...rest } = this.addressForm.value;
        this.addressForm.patchValue({
            timezonename: timezone?.title,
            timezoneid: timezone?.value,
        });
        this.address$.next(
            Object.assign(this.address$.value, {
                ...rest,
                timezoneid: timezone?.value,
                timezonename: timezone?.title,
            })
        );
    }

    lookupAddress(lookupType = "") {
        // perform the lookup and return the value emitted by dialogRef.afterClosed()
        let addressSub = null; // to avoid unsubscribing before initialization
        const params: LookupAddressParams = {
            address: this.addressForm.controls.address.value,
            address2: this.addressForm.controls.address2.value,
            country: this.addressForm.controls.country.value,
        };

        if (lookupType === "city") {
            params.city = this.addressForm.controls.city.value;
        } else {
            params.zipcode = this.addressForm.controls.zipcode.value;
        }

        addressSub = (
            !this.isReadOnly
                ? this.addressUtilityService.lookupAddress(params).pipe(switchMap(addresses => this.openDialog(addresses, params)))
                : of(null)
        ).subscribe((selectedAddress: Address) => {
            this.processSelectedAddress(selectedAddress);
            if (addressSub) {
                addressSub.unsubscribe();
            }
        });
    }

    lookupAddressByZip(lookupType = "") {
        // perform the lookup and return the value emitted by dialogRef.afterClosed()
        let addressSub = null; // to avoid unsubscribing before initialization
        const params = { zipcode: this.addressForm.controls.zipcode.value };

        addressSub = (!this.isReadOnly ? this.addressUtilityService.lookupAddress(params).pipe(delay(500)) : of(null)).subscribe(
            (selectedAddress: Address) => {
                this.processSelectedAddress(selectedAddress[0]);
                if (addressSub) {
                    addressSub.unsubscribe();
                }
            }
        );
    }

    openDialog(addresses: any[], params: LookupAddressParams) {
        const dialogRef = this.dialog.open(AddressChooserComponent, {
            maxWidth: 500,
            panelClass: ["dialog-no-padding", "dialog-no-border"],
        });
        dialogRef.componentInstance.addresses = addresses;
        dialogRef.componentInstance.selectedAddress = addresses[0];
        dialogRef.componentInstance.city = params.city || "";
        dialogRef.componentInstance.country = params.country || "";
        dialogRef.componentInstance.zipcode = params.zipcode || "";
        return dialogRef.afterClosed();
    }

    processSelectedAddress(selectedAddress: Address) {
        if (selectedAddress) {
            // next the subject with a combo of the existing address key/values
            // overwritten with the selected address key/values
            selectedAddress.id = this.address$.value.id;

            // Some lookups may return no street results. In those cases, don't override the user input.
            selectedAddress.address = selectedAddress.address || this.addressForm.controls.address.value;
            selectedAddress.address2 = selectedAddress.address2 || this.addressForm.controls.address2.value;
            selectedAddress.city = selectedAddress.city || this.addressForm.controls.city.value;
            selectedAddress.area = this.addressForm.controls.area.value;
            selectedAddress.typeid = this.addressForm.controls.typeid.value;
            selectedAddress.builderid = this.addressForm.controls.builderid.value;
            selectedAddress.communityid = this.addressForm.controls.communityid.value;
            selectedAddress.gateaccesscode = this.addressForm.controls.gateaccesscode.value;
            const timezone = this.timezones.find(zone => zone.value === selectedAddress.timezoneid);
            this.addressForm.patchValue({
                ...selectedAddress,
                timezonename: timezone?.title,
                timezoneid: timezone?.value,
                timezone,
            });
            this.address$.next(
                Object.assign(this.address$.value, {
                    ...selectedAddress,
                    timezonename: timezone?.title,
                })
            );
            this.lookupComplete = true;
            this.onUpdateLookupStatus.emit(this.lookupComplete);

            if (selectedAddress.state) {
                this.addressForm.controls.state.setValidators([Validators.required]);
            } else {
                this.addressForm.controls.state.setValidators([]);
            }
            this.addressFound$.next(true);
        }
    }

    toggleAdditionalFields() {
        this.shouldShowAdditionalFields = !this.shouldShowAdditionalFields;
    }
}
